/**
 * Phase 4 slice 4 — Inspection Requests list.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ClipboardCheck } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';

const STATUS_TONE = {
  pending: 'bg-slate-100 text-slate-600 border-slate-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  passed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  passed_with_observations: 'bg-amber-50 text-amber-700 border-amber-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-slate-100 text-slate-400 border-slate-200',
};
import { formatDate as fmt } from '../../lib/format';
import { GRNInspectDialog } from '../../components/stores/GRNInspectDialog';

export default function InspectionRequests() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [status, setStatus] = useState('');
  const [stage, setStage] = useState('');
  const [rows, setRows] = useState([]);
  const [inspectGrnId, setInspectGrnId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => { apiGet('/projects').then(setProjects).catch(() => toast.error('Failed to load projects')); }, []);
  useEffect(() => {
    const q = [projectId && `project_id=${projectId}`, status && `status=${status}`, stage && `stage=${encodeURIComponent(stage)}`].filter(Boolean).join('&');
    apiGet(`/quality/inspection-requests${q ? `?${q}` : ''}`).then((d) => setRows(d.inspection_requests || [])).catch(() => {});
  }, [projectId, status, stage, reloadKey]);

  return (
    <div>
      <PageHeader overline="Quality" title="Inspection Requests" subtitle="Stage inspections with checklists — a failed inspection auto-raises an NCR"
        actions={(
          <button onClick={() => navigate('/app/quality/inspection-requests/new')} className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-display font-bold uppercase tracking-wider rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e]" data-testid="insp-new-nav"><Plus size={14} /> New Inspection</button>
        )}
      />
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[220px]" data-testid="insp-filter-project">
          <option value="">All projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
        </select>
        <select value={stage} onChange={(e) => setStage(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid="insp-filter-stage">
          <option value="">All stages</option>{['Pre-Inspection', 'WIP', 'Final'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid="insp-filter-status">
          <option value="">All statuses</option>{Object.keys(STATUS_TONE).map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full dense-table" data-testid="inspection-table">
            <thead><tr><th>Activity</th><th>ITP Link</th><th>Stage</th><th>Inspector</th><th>Raised</th><th className="text-center">Checklist</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={8} className="text-center text-sm text-slate-400 py-10"><ClipboardCheck size={20} className="inline mr-2 opacity-40" />No inspection requests.</td></tr>
                : rows.map((r) => {
                  const failed = (r.checklist_items || []).filter((i) => i.status === 'fail').length;
                  return (
                    <tr key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/app/quality/inspection-requests/${r.id}`)} data-testid={`insp-row-${r.id}`}>
                      <td className="text-[13px] font-display font-semibold max-w-[200px] truncate">{r.activity || '—'}</td>
                      <td>
                        {r.itp_id && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-violet-50 border border-violet-200 text-violet-700 rounded font-bold"
                            title={`ITP: ${r.itp_discipline} · ${r.itp_activity}`}
                            data-testid={`insp-itp-chip-${r.id}`}>
                            ITP·{r.itp_discipline} · {(r.itp_activity || '').slice(0, 22)}{r.itp_activity?.length > 22 ? '…' : ''}
                          </span>
                        )}
                      </td>
                      <td className="text-xs text-slate-600">{r.stage}</td>
                      <td className="text-xs text-slate-500">{r.inspector_role}</td>
                      <td className="text-xs text-slate-500">{fmt(r.raised_at)}</td>
                      <td className="text-center text-xs">{(r.checklist_items || []).length}{failed > 0 && <span className="text-red-600 font-bold"> · {failed} fail</span>}</td>
                      <td><span className={`tag border ${STATUS_TONE[r.status]}`}>{(r.status || '').replace(/_/g, ' ')}</span></td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {r.source === 'grn' && r.source_grn_id && !['passed', 'passed_with_observations', 'failed', 'cancelled'].includes(r.status) && (
                          <button onClick={() => setInspectGrnId(r.source_grn_id)} className="px-2 py-1 text-[11px] font-display font-bold rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e] whitespace-nowrap" data-testid={`insp-inspect-grn-${r.id}`}>Inspect GRN</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
      {inspectGrnId && <GRNInspectDialog grnId={inspectGrnId} onClose={() => setInspectGrnId(null)} onInspected={() => setReloadKey(k => k + 1)} />}
    </div>
  );
}
